/**
 * Country list for the phone-entry country picker.
 * - `iso` is the ISO 3166-1 alpha-2 code (used by libphonenumber-js for parsing).
 * - `dial` is the country calling code (without the "+").
 * - `flag` is the emoji regional indicator.
 * Ordering: Nigeria first, then popular African markets, then rest alphabetical.
 */

const flagFor = (iso) =>
  iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

const PRIORITY = ["NG", "GH", "KE", "ZA", "EG", "RW", "UG", "TZ", "ET"];

// (name, iso, dial). Comprehensive-enough for a signup form (~80 countries).
const RAW = [
  ["Nigeria", "NG", "234"],
  ["Ghana", "GH", "233"],
  ["Kenya", "KE", "254"],
  ["South Africa", "ZA", "27"],
  ["Egypt", "EG", "20"],
  ["Rwanda", "RW", "250"],
  ["Uganda", "UG", "256"],
  ["Tanzania", "TZ", "255"],
  ["Ethiopia", "ET", "251"],
  ["Algeria", "DZ", "213"],
  ["Angola", "AO", "244"],
  ["Argentina", "AR", "54"],
  ["Australia", "AU", "61"],
  ["Austria", "AT", "43"],
  ["Bahrain", "BH", "973"],
  ["Bangladesh", "BD", "880"],
  ["Belgium", "BE", "32"],
  ["Benin", "BJ", "229"],
  ["Botswana", "BW", "267"],
  ["Brazil", "BR", "55"],
  ["Burkina Faso", "BF", "226"],
  ["Cameroon", "CM", "237"],
  ["Canada", "CA", "1"],
  ["Chad", "TD", "235"],
  ["Chile", "CL", "56"],
  ["China", "CN", "86"],
  ["Colombia", "CO", "57"],
  ["Côte d'Ivoire", "CI", "225"],
  ["Denmark", "DK", "45"],
  ["Finland", "FI", "358"],
  ["France", "FR", "33"],
  ["Gabon", "GA", "241"],
  ["Gambia", "GM", "220"],
  ["Germany", "DE", "49"],
  ["Greece", "GR", "30"],
  ["Guinea", "GN", "224"],
  ["Hong Kong", "HK", "852"],
  ["India", "IN", "91"],
  ["Indonesia", "ID", "62"],
  ["Ireland", "IE", "353"],
  ["Israel", "IL", "972"],
  ["Italy", "IT", "39"],
  ["Japan", "JP", "81"],
  ["Jordan", "JO", "962"],
  ["Kuwait", "KW", "965"],
  ["Lebanon", "LB", "961"],
  ["Liberia", "LR", "231"],
  ["Libya", "LY", "218"],
  ["Malawi", "MW", "265"],
  ["Malaysia", "MY", "60"],
  ["Mali", "ML", "223"],
  ["Mauritius", "MU", "230"],
  ["Mexico", "MX", "52"],
  ["Morocco", "MA", "212"],
  ["Mozambique", "MZ", "258"],
  ["Namibia", "NA", "264"],
  ["Netherlands", "NL", "31"],
  ["New Zealand", "NZ", "64"],
  ["Niger", "NE", "227"],
  ["Norway", "NO", "47"],
  ["Oman", "OM", "968"],
  ["Pakistan", "PK", "92"],
  ["Philippines", "PH", "63"],
  ["Poland", "PL", "48"],
  ["Portugal", "PT", "351"],
  ["Qatar", "QA", "974"],
  ["Saudi Arabia", "SA", "966"],
  ["Senegal", "SN", "221"],
  ["Sierra Leone", "SL", "232"],
  ["Singapore", "SG", "65"],
  ["Somalia", "SO", "252"],
  ["South Korea", "KR", "82"],
  ["Spain", "ES", "34"],
  ["Sudan", "SD", "249"],
  ["Sweden", "SE", "46"],
  ["Switzerland", "CH", "41"],
  ["Togo", "TG", "228"],
  ["Tunisia", "TN", "216"],
  ["Turkey", "TR", "90"],
  ["Ukraine", "UA", "380"],
  ["United Arab Emirates", "AE", "971"],
  ["United Kingdom", "GB", "44"],
  ["United States", "US", "1"],
  ["Vietnam", "VN", "84"],
  ["Zambia", "ZM", "260"],
  ["Zimbabwe", "ZW", "263"],
];

const ALL = RAW.map(([name, iso, dial]) => ({
  name,
  iso,
  dial,
  flag: flagFor(iso),
}));

const priorityOrdered = PRIORITY.map((iso) => ALL.find((c) => c.iso === iso)).filter(Boolean);
const rest = ALL.filter((c) => !PRIORITY.includes(c.iso)).sort((a, b) =>
  a.name.localeCompare(b.name)
);

export const COUNTRIES = [...priorityOrdered, ...rest];
export const DEFAULT_COUNTRY = COUNTRIES[0]; // Nigeria

// Common local phone placeholders per country for the input hint.
// Kept small and readable; libphonenumber-js formats live as the user types.
export const PLACEHOLDERS = {
  NG: "801 234 5678",
  GH: "24 123 4567",
  KE: "712 345678",
  ZA: "71 123 4567",
  EG: "100 123 4567",
  RW: "78 123 4567",
  UG: "700 123456",
  TZ: "621 234 567",
  ET: "91 123 4567",
  US: "(555) 123-4567",
  GB: "7911 123456",
  IN: "81234 56789",
  CA: "(555) 123-4567",
  FR: "6 12 34 56 78",
  DE: "1512 3456789",
};
